import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {TagSchema} from 'constants/RegistryConstants';
import {Batch, BatchSchema, Storable} from 'constants/SampleConstants';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {ListingRendererType, ListingTypes} from 'nav/expanded/ListingRendererConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {getLinksKey} from 'schemas/tags/TagUtil';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import {FILTERS} from 'search/SearchConstants';
import {isLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {getChipsRowHeight} from 'ui-library/chips/ChipList';
import * as ChipUtil from 'ui-library/chips/ChipUtil';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';

import * as TableTagUtil from './TableTagUtil';
import TagValue, {CollapsedChipsFieldValue, TagForTableDisplay} from './TagValue';

type ListableFolderItem = ListingTypes[ListingRendererType.FOLDER_ITEM];

const CHIP_SIZE = ChipSize.SMALL;

function _extraFieldAccessorKey(
  fieldName: string,
  schemaFieldId?: number,
  mergeBySchemaFieldName?: boolean
): string {
  const baseAccessorKey = `${ScrollableTableConstants.EXTRA_FIELD_PREFIX}${fieldName}`;
  return schemaFieldId === undefined || mergeBySchemaFieldName
    ? baseAccessorKey
    : `${baseAccessorKey}:fromSchemaFieldId:${schemaFieldId}`;
}

function mapMultipleFieldValuesForColumn({
  fieldValues,
  maxChipsPerRow,
  collapsedMultiLinkOverflowElement,
}: {
  fieldValues: Array<TagForTableDisplay>;
  maxChipsPerRow?: number;
  collapsedMultiLinkOverflowElement?: (numLinks: number) => JSX.Element;
}): JSX.Element {
  if (fieldValues.length > 1 && fieldValues.every((val) => ChipUtil.tagShouldRenderChips(val))) {
    return (
      <CollapsedChipsFieldValue
        fieldValues={fieldValues}
        maxChipsPerRow={maxChipsPerRow}
        collapsedMultiLinkOverflowElement={collapsedMultiLinkOverflowElement}
      />
    );
  }
  return (
    <div>
      {fieldValues.map((val, index) => (
        <TagValue tag={val} key={index} maxChipsPerRow={maxChipsPerRow} />
      ))}
    </div>
  );
}

export function mapTagsToColumns({
  tags,
  tagColumns,
  multipleTagsPerColumn = false,
  mergeBySchemaFieldName = true,
  maxChipsPerRow,
  collapsedMultiLinkOverflowElement,
}: {
  tags: Array<TagForTableDisplay>;
  tagColumns: Array<ScrollableTableConstants.ScrollableTableColumn>;
  multipleTagsPerColumn?: boolean;
  mergeBySchemaFieldName?: boolean;
  maxChipsPerRow?: number;
  collapsedMultiLinkOverflowElement?: (numLinks: number) => JSX.Element;
}): {tagFields: {[accessorKey: string]: JSX.Element}} {
  const tagsByColumn: {[accessorKey: string]: Array<TagForTableDisplay>} = _.object(
    tagColumns.map((tagColumn) => {
      let matchingTags: Array<TagForTableDisplay> = [];
      if (tagColumn.accessorKey.startsWith(ScrollableTableConstants.EXTRA_FIELD_PREFIX)) {
        matchingTags = tags.filter(
          (tag) =>
            _extraFieldAccessorKey(
              tag.name,
              isSchemaTag(tag) ? tag.tagSchemaField.id : undefined,
              mergeBySchemaFieldName
            ) === tagColumn.accessorKey
        );
      }
      return [tagColumn.accessorKey, matchingTags];
    })
  );

  return {
    tagFields: _.mapObject(tagsByColumn, (vals, key) =>
      multipleTagsPerColumn ? (
        mapMultipleFieldValuesForColumn({
          fieldValues: vals,
          maxChipsPerRow,
          collapsedMultiLinkOverflowElement,
        })
      ) : (
        <TagValue tag={vals[0]} maxChipsPerRow={maxChipsPerRow} />
      )
    ),
  };
}

function _getSortedFileSchemaFieldTagColumnsData(
  files: Array<ListableFolderItem>,
  mergeBySchemaFieldName: boolean = true
): {fileSchemaFieldTagColumnsData: Array<TableTagUtil.TagColumnData>; fileResultFieldNames: Set<unknown>} {
  // Return all fields of the files' tag schemas, sorted by most-to-least frequent schema and increasing
  // field position.
  // eslint-disable-next-line no-restricted-syntax
  const schemas = _.pluck(files, 'tagSchema');
  const fileResultFieldNames = new Set(); // TODO(T8967): Remove
  const schemaFieldTagColumnsData = TableTagUtil.getSortedSchemaFieldTagColumnsData(
    schemas,
    mergeBySchemaFieldName
  );
  return {
    fileSchemaFieldTagColumnsData: schemaFieldTagColumnsData,
    fileResultFieldNames,
  };
}

export function getFolderItemTags(item: {id: string; tags?: Array<Tag>; tagsJson?: Array<Tag>}): Array<Tag> {
  if (item.tags) {
    return item.tags;
  } else if (item.tagsJson) {
    return item.tagsJson;
  } else {
    assert(false, 'Item must have either tags or tagsJson');
  }
}

function _getSortedTagColumnsData(
  items: Array<ListableFolderItem>,
  options: {schemaFieldNames?: Array<string>} = {}
): Array<TableTagUtil.TagColumnData> {
  const schemaFieldNames = options.schemaFieldNames || [];
  const tags = _.chain(items)
    .map((item) => getFolderItemTags(item))
    .flatten() // eslint-disable-next-line no-restricted-syntax
    .reject((tag) => _.contains(schemaFieldNames, tag.name)) // custom -> not schema
    .value();
  const tagCounts = _.countBy(tags, 'name');
  const sortedTags = _.chain(tags)
    .sortBy((tag) => tagCounts[tag.name])
    .reverse()
    .uniq(({name}) => name)
    .value();

  const tagColumnsData = sortedTags.map((tag) => {
    const rv: TableTagUtil.TagColumnData = {name: tag.name};
    if (isSchemaTag(tag)) {
      rv.tagSchemaFieldId = tag.tagSchemaField.id;
    }
    return rv;
  });
  return tagColumnsData;
}

export const sortKeyPrefix = FILTERS.TAG.prefix!;
const CONTENTS_SCHEMA_SORT_KEY_PREFIX = `contents_${sortKeyPrefix}`;

function getSortKeyFromTagName(tagName: string): string {
  return `${sortKeyPrefix}${tagName}`;
}

export function isTagSortKey(sortKey: string): boolean {
  return sortKey.startsWith(sortKeyPrefix);
}

export function isContentsTagSortKey(sortKey: string): boolean {
  return sortKey.startsWith(CONTENTS_SCHEMA_SORT_KEY_PREFIX);
}

export function getTagNameFromSortKey(sortKey: string): string {
  return sortKey.split(sortKeyPrefix).pop()!;
}

export function getTagIdFromSortKey(sortKey: string): number {
  return Number(sortKey.split('fromSchemaFieldId:').pop()!);
}

const SCHEMA_TAG_PREFIX = 'schema_tag:';
const CUSTOM_TAG_PREFIX = 'custom_tag:';

export function isSchemaTagColumnKey(columnKey: string | undefined): boolean {
  return columnKey != null && columnKey.startsWith(SCHEMA_TAG_PREFIX);
}

export function isCustomTagColumnKey(columnKey: string | undefined): boolean {
  return columnKey != null && columnKey.startsWith(CUSTOM_TAG_PREFIX);
}

export function getSchemaFieldIdFromColumnKey(columnKey: string | undefined): string {
  return columnKey?.split(SCHEMA_TAG_PREFIX).pop() ?? '';
}

function getColumnKeyFromTag(tagColumnData: TableTagUtil.TagColumnData): string {
  // Multiple schema fields can have the same name. We want to display data for these fields in the
  // same column (accessorKey), but save preferences on a schema field basis (columnKey).
  // For example, a user may have a "Description" field for both containers and locations, where the
  // container description is very short and the location description is very long. Optimize for
  // specifying widths per schema field, but render the data in the same column if they do show in
  // the same view.
  return tagColumnData.tagSchemaFieldId
    ? `${SCHEMA_TAG_PREFIX}${tagColumnData.tagSchemaFieldId}`
    : `${CUSTOM_TAG_PREFIX}${tagColumnData.name}`;
}

/**
 * Given tagColumnsData, return column dicts to render through ScrollableTable.
 *
 * @param tagColumnsData - Array of tagColumnData, data used for constructing the column dict for a tag.
 * Must include name and optionally tagSchemaFieldId for schema fields.
 * @param mergeBySchemaFieldName - Optional boolean (default true) to merge by field name; when false, add
 * schema id to accessorKey so fields with duplicate names across schemas are kept separate
 */
function _getColumnsForTags(
  tagColumnsData: Array<TableTagUtil.TagColumnData>,
  resultFieldNames: Set<unknown>,
  mergeBySchemaFieldName: boolean = true
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  return tagColumnsData.map((tagColumnData) => {
    const accessorKey = resultFieldNames.has(tagColumnData.name)
      ? `${ScrollableTableConstants.RESULT_FIELD_PREFIX}${tagColumnData.name}`
      : _extraFieldAccessorKey(tagColumnData.name, tagColumnData.tagSchemaFieldId, mergeBySchemaFieldName);
    const columnKey = getColumnKeyFromTag(tagColumnData);
    const sortKey = getSortKeyFromTagName(tagColumnData.name);
    return {
      header: tagColumnData.displayName || tagColumnData.name,
      icon: 'tag',
      accessorKey,
      columnKey,
      flexGrow: 1,
      isCollapsible: true,
      hidden: false,
      isSortable:
        !resultFieldNames.has(tagColumnData.name) &&
        (!isLinkFieldType(tagColumnData.fieldType!) || tagColumnData.fieldType === FieldType.SELECTOR),
      sortKey,
    };
  });
}

export function listFileTagColumns(
  files: Array<ListableFolderItem>,
  {
    includeCustomTagColumns = true,
    mergeBySchemaFieldName = true,
  }: {
    includeCustomTagColumns?: boolean;
    mergeBySchemaFieldName?: boolean;
  } = {}
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  // Return all fields of the files' sorted tag schemas, then all custom tags sorted by most-to-least
  // frequent.
  const {fileSchemaFieldTagColumnsData, fileResultFieldNames} = _getSortedFileSchemaFieldTagColumnsData(
    files,
    mergeBySchemaFieldName
  );
  if (includeCustomTagColumns) {
    const fileSchemaFieldNames = fileSchemaFieldTagColumnsData.map(({name}) => name);
    const customTagColumnsData = _getSortedTagColumnsData(files, {schemaFieldNames: fileSchemaFieldNames});
    const tagColumnsData = fileSchemaFieldTagColumnsData.concat(customTagColumnsData);
    return _getColumnsForTags(tagColumnsData, fileResultFieldNames, mergeBySchemaFieldName);
  }
  return _getColumnsForTags(fileSchemaFieldTagColumnsData, fileResultFieldNames, mergeBySchemaFieldName);
}

export function listBatchTagColumns(
  batches: Array<Batch>,
  batchSchemasById: {[batchSchemaId: string]: BatchSchema}
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  const batchSchemas = batches.map(({batchSchemaId}) => batchSchemasById[batchSchemaId]);
  const schemaFieldTagColumnsData = TableTagUtil.getSortedSchemaFieldTagColumnsData(batchSchemas);
  const batchColumns = _getColumnsForTags(schemaFieldTagColumnsData, new Set());

  // Get schema tags and custom tags for the batches' files
  // eslint-disable-next-line no-restricted-syntax
  const files = _.pluck(batches, 'file');
  const {fileSchemaFieldTagColumnsData, fileResultFieldNames} =
    _getSortedFileSchemaFieldTagColumnsData(files);
  const fileColumns = _getColumnsForTags(fileSchemaFieldTagColumnsData, fileResultFieldNames);

  return batchColumns.concat(fileColumns);
}

export function listStorableTagColumns(
  storables: Array<Storable>
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  // Return all fields of the storables schemas, sorted by most-to-least frequent type, then most-to-least
  // frequent schema, and increasing field position.
  const storableTypeCounts = _.countBy(storables, (storable) => APIUtil.apiIdToType(storable.id));
  const storableTypes = Object.keys(storableTypeCounts);
  const storableFieldTagColumnsDataWithDuplicates = storableTypes
    .sort((type1, type2) => storableTypeCounts[type1] - storableTypeCounts[type2])
    .reverse() // most-to-least frequent storable type
    .reduce((tagColumnsData, type) => {
      // return sorted schema field names for schemas of that type
      const storableSchemas = _.chain(storables)
        .filter((storable) => type === APIUtil.apiIdToType(storable.id))
        .map((storable) => storable.storableSchema)
        .value();
      const schemaFieldTagColumnsData = TableTagUtil.getSortedSchemaFieldTagColumnsData(storableSchemas);
      return tagColumnsData.concat(schemaFieldTagColumnsData);
    }, [] as Array<TableTagUtil.TagColumnData>);
  // eslint-disable-next-line no-restricted-syntax
  const storableFieldTagColumnsData = _.uniq(storableFieldTagColumnsDataWithDuplicates, ({name}) => name);
  return _getColumnsForTags(storableFieldTagColumnsData, new Set());
}

export function listContainableSchemaFieldColumns(
  tagSchemas: Array<TagSchema>
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  return _getColumnsForTags(TableTagUtil.getSortedSchemaFieldTagColumnsData(tagSchemas), new Set());
}

export function listContainableSchemaFieldColumnsGraphQL(
  tagSchemas: Array<TagSchemaSummaryWithFieldsGraphQL>
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  return _getColumnsForTags(TableTagUtil.getSortedSchemaFieldTagColumnsDataGraphQL(tagSchemas), new Set());
}

export function listSortedTagColumns(
  items: Array<{schema: TableTagUtil.BaseSchema}>
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  // eslint-disable-next-line no-restricted-syntax
  const schemas = _.pluck(items, 'schema');
  const schemaFieldTagColumnsData = TableTagUtil.getSortedSchemaFieldTagColumnsData(schemas);
  return _getColumnsForTags(schemaFieldTagColumnsData, new Set());
}

/**
 * Computes the total row height for a set of tags. If a tag has multiple chips, they are
 * rendered on separate lines, which requires the total row height to be greater than the default value.
 * If there are no chips, just return the default row height value.
 */
export function getRowHeight(tags: Array<Tag>, maxChipsPerRow?: number): number {
  const numLinks = _.max(
    tags.map((tag) =>
      isSchemaTag(tag) && ChipUtil.tagShouldRenderChips(tag)
        ? (tag[getLinksKey(tag.tagSchemaField.fieldType)] as Array<unknown>).length
        : 0
    )
  );
  return getChipsRowHeight(maxChipsPerRow ? Math.min(numLinks, maxChipsPerRow + 1) : numLinks, CHIP_SIZE);
}

// We optionally show validation errors as a column. It is not a tag column but is listed with
// the other tag columns.
export const validationErrorsColumn = {
  header: 'Validation Errors',
  accessorKey: 'validationErrors',
  flexGrow: 1,
  hidden: true,
};
